// src/pages/Connector/GoogleDrive/ListGoogleDriveFiles.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import DataTableWithFilters from "../../../../components/Common/DataTableWithFilters";
import EditGoogleDriveFileContent from "./EditGoogleDriveFileContent";
import EditGoogleDriveSummary from "./EditGoogleDriveSummary";
import SyncSettingsModalForDrive from "./SyncSettingsModalForDrive";
import PendingUpdateModalForDrive from "./PendingUpdateModalForDrive";
import ManageGDriveCategories from "./ManageGDriveCategories";

import {
    Tooltip,
    Modal as MuiModal,
    Box,
    Button as MuiButton,
    useTheme,
    Stack,
    FormControl,
    InputLabel,
    Select as MuiSelect,
    MenuItem,
    Chip,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import LaunchIcon from '@mui/icons-material/Launch';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // <-- Import ExpandMoreIcon
import { Select as AntdSelect } from 'antd';
import { marked } from 'marked';

const { Option } = AntdSelect;

const ExpandedComponent = ({ data }) => (
    <Box sx={{ p: 2, backgroundColor: 'action.hover', borderLeft: '3px solid', borderColor: 'primary.main' }}>
        <Grid container spacing={1.5} sx={{ fontSize: '0.9em' }}>
            {/* --- Row 1: Basic Info --- */}
            <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>DB ID:</strong> {data.id || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Filename:</strong> {data.filename || 'N/A'}</Typography>
            </Grid>

            {/* --- Row 2: Statuses --- */}
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Scraped:</strong> {data.is_scrape ? 'Yes' : 'No'}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Trained:</strong> {data.is_trained ? 'Yes' : 'No'}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Manually Edited:</strong> {data.is_manually_edited ? 'Yes' : 'No'}</Typography></Grid>
            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Pending Update:</strong> {data.has_pending_update ? 'Yes' : 'No'}</Typography></Grid>

            {/* --- Row 3: Summary (Rendered as HTML) --- */}
            <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Summary:</Typography>
                <Box 
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.5, maxHeight: 150, overflowY: 'auto', bgcolor: 'background.paper' }}
                    dangerouslySetInnerHTML={{ __html: data.summary ? marked.parse(data.summary) : 'N/A' }} 
                />
            </Grid>

            {/* --- Row 4: Page Content Preview (renders HTML correctly) --- */}
            <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Page Content:</Typography>
                <Box 
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.5, maxHeight: 200, overflowY: 'auto', bgcolor: 'background.paper' }}
                    dangerouslySetInnerHTML={{ __html: data.page_content ? marked.parse(data.page_content) : 'N/A' }}
                />
            </Grid>

            {/* --- Row 5: Pending Content Preview (Conditional) --- */}
            {data.has_pending_update && data.pending_update_content && (
                <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>Pending Content Preview:</Typography>
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.light', borderRadius: 1, mt: 0.5, maxHeight: 150, overflowY: 'auto', bgcolor: '#fffbe6' }}>
                        <Typography variant="body2">{data.pending_update_content.substring(0, 300)}...</Typography>
                    </Box>
                </Grid>
            )}
        </Grid>
    </Box>
);

export default function ListGoogleDriveFiles({ refreshTrigger, chatbotId, permission, categories, onCategoriesUpdate }) {
    const theme = useTheme();
    const [files, setFiles] = useState([]);
    const [filteredFiles, setFilteredFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isProcessingScrapeTrain, setIsProcessingScrapeTrain] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    const [isSyncSettingsModalOpen, setIsSyncSettingsModalOpen] = useState(false);
    const [isPendingUpdateModalOpen, setIsPendingUpdateModalOpen] = useState(false);
    const [currentDocumentForModal, setCurrentDocumentForModal] = useState(null);
    const [refreshingRows, setRefreshingRows] = useState({});
    const [actionLoading, setActionLoading] = useState(null);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

    // State for in-line editing
    const [editingRowId, setEditingRowId] = useState(null);
    const [tempSelectedCategories, setTempSelectedCategories] = useState([]);
    const [isRowSaving, setIsRowSaving] = useState(false);

    // Filter states
    const [categoryFilter, setCategoryFilter] = useState([]);
    const [tagsFilter, setTagsFilter] = useState([]);
    const [allTags, setAllTags] = useState([]);
    const [trainFilter, setTrainFilter] = useState('all');
    const [autoSyncFilter, setAutoSyncFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    
    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    const muiModalStyle = { 
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '70vw' }, maxWidth: '1000px', 
        maxHeight: '90vh', bgcolor: 'background.paper',
        border: 'none', borderRadius: '8px', boxShadow: 24, p: 0,
        display: 'flex', flexDirection: 'column', outline: 'none',
        overflow: 'hidden' // Ensures content stays within modal bounds
    };

    const summaryModalStyle = { 
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '70%', md: '700px' }, maxWidth: '90vw', 
        maxHeight: '90vh', bgcolor: 'background.paper',
        border: 'none', borderRadius: '8px', boxShadow: 24, p: 0,
        display: 'flex', flexDirection: 'column', outline: 'none',
        overflow: 'hidden', // Ensures content stays within modal bounds
    };

    const fetchFiles = useCallback(async () => {
        setLoading(true); 
        setError("");
        try {
            const response = await axiosInstance.get(`/api/google-drive/synced-files`, { params });
            setFiles(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Error fetching synced files.");
            setFiles([]);
        } finally {
            setLoading(false);
        }
    }, [chatbotId]);

    useEffect(() => { fetchFiles(); }, [refreshTrigger, fetchFiles]);
    
    useEffect(() => {
        const tags = new Set();
        files.forEach(doc => doc.tags && doc.tags.split(',').forEach(tag => tag.trim() && tags.add(tag.trim())));
        setAllTags(Array.from(tags).sort());
    }, [files]);

    useEffect(() => {
        const filtered = files.filter((file) => {
            const lowerSearchText = searchText.toLowerCase();
            const matchesSearch = !searchText || Object.values(file).some(val => String(val).toLowerCase().includes(lowerSearchText));
            const matchesCategory = categoryFilter.length === 0 || file.categories?.some(cat => categoryFilter.includes(cat.id));
            const matchesTags = tagsFilter.length === 0 || tagsFilter.every(tag => file.tags?.toLowerCase().includes(tag.toLowerCase()));
            const matchesTrain = trainFilter === 'all' || (trainFilter === 'trained' ? file.is_trained : !file.is_trained);
            const matchesAutoSync = autoSyncFilter === 'all' || (autoSyncFilter === 'on' ? file.is_auto_sync : !file.is_auto_sync);
            const matchesStatus = statusFilter === 'all' || (statusFilter === 'pending' ? file.has_pending_update : !file.has_pending_update);
            return matchesSearch && matchesCategory && matchesTags && matchesTrain && matchesAutoSync && matchesStatus;
        });
        setFilteredFiles(filtered);
    }, [searchText, files, categoryFilter, tagsFilter, trainFilter, autoSyncFilter, statusFilter]);

    // --- In-line editing handlers ---
    const handleEditClick = (row) => {
        setEditingRowId(row.id);
        setTempSelectedCategories(row.categories?.map(c => c.id) || []);
    };
    const handleCancelClick = () => {
        setEditingRowId(null);
        setTempSelectedCategories([]);
    };
    const handleSaveClick = async (rowId) => {
        setIsRowSaving(true);
        setError("");
        try {
            const response = await axiosInstance.put(`/api/google-drive/file/${rowId}/categories`, { category_ids: tempSelectedCategories }, { params });
            setFiles(prevFiles => prevFiles.map(file => 
                file.id === rowId ? { ...file, categories: response.data.data.categories } : file
            ));
            setEditingRowId(null);
            setError("Categories updated successfully.");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to update categories.");
        } finally {
            setIsRowSaving(false);
        }
    };

    const handleRefreshFile = async (dbFileId) => {
        setRefreshingRows(prev => ({ ...prev, [dbFileId]: true }));
        setError("");
        try {
            const response = await axiosInstance.post(`/api/google-drive/refresh-file/${dbFileId}`, {}, { params });
            setFiles(prev => prev.map(f => (f.id === dbFileId ? { ...f, ...response.data.data } : f)));
            setError(`File refreshed successfully.`);
        } catch (err) {
            setError(err.response?.data?.message || `Error refreshing file.`);
        } finally {
            setRefreshingRows(prev => ({ ...prev, [dbFileId]: false }));
        }
    };

    const handleDeleteFile = async (dbFileId, filename) => {
        if (!window.confirm(`Are you sure you want to remove "${filename}" from the synced list?`)) return;
        setLoading(true); 
        setError("");
        try {
            await axiosInstance.delete(`/api/google-drive/delete-file/${dbFileId}`, { params });
            setFiles(prev => prev.filter((f) => f.id !== dbFileId));
            setError(`"${filename}" removed successfully.`);
        } catch (err) {
            setError(err.response?.data?.message || `Error removing file.`);
        } finally {
            setLoading(false);
        }
    };

    const handleScrapeAndTrain = async () => {
        const processableFiles = selectedRows.filter(row => !row.is_trained || row.is_manually_edited);
        if (processableFiles.length === 0) {
            setError("No files selected, or all selected files are already trained.");
            return;
        }
        setIsProcessingScrapeTrain(true);
        setError(''); 
        try {
            const fileIdsToProcess = processableFiles.map(row => row.google_file_id);
            const response = await axiosInstance.post(`/api/google-drive/scrape-and-train`, { fileIds: fileIdsToProcess }, { params });
            setError(response.data.message || 'Files processed successfully.'); 
            fetchFiles(); 
            setSelectedRows([]); 
            setToggleCleared(prev => !prev); 
        } catch (err) {
            setError(err.response?.data?.message || "Failed to process files.");
        } finally {
            setIsProcessingScrapeTrain(false); 
        }
    };

    const handleRowSelected = ({ selectedRows }) => setSelectedRows(selectedRows);
    
    // Edit Content Modal handlers
    const openEditModal = (doc) => { setCurrentDocumentForModal(doc); setIsEditModalOpen(true); };
    const closeEditModal = () => { setIsEditModalOpen(false); setCurrentDocumentForModal(null); };
    const handleUpdateFromEdit = () => { fetchFiles(); closeEditModal(); setError("Content updated successfully."); };

    // Edit Summary Modal handlers
    const openSummaryModal = (doc) => { setCurrentDocumentForModal(doc); setIsSummaryModalOpen(true); };
    const closeSummaryModal = () => { setIsSummaryModalOpen(false); setCurrentDocumentForModal(null); };
    const handleUpdateFromSummary = () => { fetchFiles(); closeSummaryModal(); setError("Summary updated successfully."); };

    const openSyncSettingsModal = (doc) => { setCurrentDocumentForModal(doc); setIsSyncSettingsModalOpen(true); };
    const closeSyncSettingsModal = () => { setIsSyncSettingsModalOpen(false); setCurrentDocumentForModal(null); };
    const handleSaveSyncSettings = async (settings) => {
        if (!currentDocumentForModal) return;
        setActionLoading('syncSave'); setError("");
        try {
            const payload = { updates: [{ id: currentDocumentForModal.id, ...settings }] };
            await axiosInstance.post(`/api/google-drive/update-sync-settings`, payload, { params });
            fetchFiles();
            closeSyncSettingsModal();
            setError("Sync settings updated successfully.");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to update sync settings.");
        } finally {
            setActionLoading(null);
        }
    };

    const openPendingUpdateModal = (doc) => { setCurrentDocumentForModal(doc); setIsPendingUpdateModalOpen(true); };
    const closePendingUpdateModal = () => { setIsPendingUpdateModalOpen(false); setCurrentDocumentForModal(null); };
    const handleResolvePendingUpdate = async (action, editedContent = null) => {
        if (!currentDocumentForModal) return;
        setActionLoading(action); setError("");
        try {
            const payload = { action, page_content: editedContent };
            await axiosInstance.post(`/api/google-drive/resolve-pending-update/${currentDocumentForModal.id}`, payload, { params });
            fetchFiles();
            closePendingUpdateModal();
            setError(`Pending update resolved.`);
        } catch (err) {
            setError(err.response?.data?.message || `Failed to resolve pending update.`);
        } finally {
            setActionLoading(null);
        }
    };

    const columns = [
        { 
            name: "File Name", 
            selector: (row) => row.filename, 
            sortable: true, 
            grow: 2, 
            wrap: true, 
            cell: row => <Tooltip title={row.filename} arrow><span>{row.filename}</span></Tooltip> 
        },
        { 
            name: "Categories", 
            selector: (row) => row.categories?.map(c => c.name).join(', ') || 'Uncategorized', 
            sortable: true, 
            wrap: true,
            minWidth: '250px',
            cell: (row) => {
                const isEditing = editingRowId === row.id;
                return isEditing ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', my: 1 }}>
                        <AntdSelect
                            mode="multiple"
                            allowClear
                            style={{ width: '100%', flexGrow: 1 }}
                            placeholder="Select categories..."
                            value={tempSelectedCategories}
                            onChange={setTempSelectedCategories}
                            disabled={isRowSaving}
                        >
                            {categories.map(cat => <Option key={cat.id} value={cat.id}>{cat.name}</Option>)}
                        </AntdSelect>
                        <IconButton size="small" color="primary" onClick={() => handleSaveClick(row.id)} disabled={isRowSaving}>
                            {isRowSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelClick} disabled={isRowSaving}><CloseIcon /></IconButton>
                    </Stack>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', '&:hover .editIcon': { opacity: 1 } }}>
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>{row.categories?.map(c => c.name).join(', ') || 'Uncategorized'}</Typography>
                        {permission.canModify && 
                            <IconButton className="editIcon" size="small" sx={{ opacity: 0, transition: 'opacity 0.2s' }} onClick={() => handleEditClick(row)} disabled={editingRowId !== null && editingRowId !== row.id}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        }
                    </Box>
                );
            }
        },
        { name: "Tags", selector: (row) => row.tags, sortable: false, wrap: true },
        { name: "Auto Sync", selector: row => row.is_auto_sync, sortable: true, center: true, cell: row => (
            <Tooltip title={row.is_auto_sync ? "Auto-sync is ON" : "Auto-sync is OFF"}><i className={`fa ${row.is_auto_sync ? 'fa-toggle-on text-success' : 'fa-toggle-off text-secondary'}`} style={{ fontSize: '1.6em' }} /></Tooltip>
        )},
        { name: "Status", center: true, cell: row => (
            row.has_pending_update ? <Chip label="Review Pending" color="warning" size="small" onClick={() => openPendingUpdateModal(row)} />
            : <Chip label={row.is_trained ? "Trained" : "Not Trained"} color={row.is_trained ? "success" : "default"} size="small" variant={row.is_trained ? "filled" : "outlined"}/>
        )},
        { 
            name: "Actions", 
            width: '200px', 
            cell: (row) => (
                <Box>
                    {permission.canModify && <>
                        <Tooltip title="Refresh & Retrain"><IconButton size="small" onClick={() => handleRefreshFile(row.id)} disabled={refreshingRows[row.id]}>{refreshingRows[row.id] ? <CircularProgress size={20}/> : <RefreshIcon />}</IconButton></Tooltip>
                        {/* Conditionally render Edit Content and Edit Summary */}
                        {row.is_trained && (
                            <>
                                <Tooltip title="Edit Content"><IconButton size="small" color="primary" onClick={() => openEditModal(row)}><EditIcon /></IconButton></Tooltip>
                                <Tooltip title="Edit Summary"><IconButton size="small" color="primary" onClick={() => openSummaryModal(row)}><DescriptionIcon /></IconButton></Tooltip>
                            </>
                        )}
                    </>}
                    <Tooltip title="Open in Google Drive"><IconButton size="small" href={row.webViewLink} target="_blank" rel="noopener noreferrer"><LaunchIcon /></IconButton></Tooltip>
                    {permission.canDelete && <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => handleDeleteFile(row.id, row.filename)}><DeleteIcon /></IconButton></Tooltip>}
                </Box>
            ), 
            ignoreRowClick: true, 
            allowOverflow: true, 
            button: true
        },
    ];

    const filterComponents = (
        <>
          {permission.canModify && <MuiButton variant="outlined" onClick={() => setIsCategoryModalOpen(true)}>Manage Categories</MuiButton>}
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}><InputLabel>Categories</InputLabel><MuiSelect multiple value={categoryFilter} label="Categories" onChange={(e) => setCategoryFilter(e.target.value)} renderValue={(s) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{s.map(v => <Chip key={v} label={categories.find(c=>c.id===v)?.name||''} size="small"/>)}</Box>}>{categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}</MuiSelect></FormControl>
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}><InputLabel>Tags</InputLabel><MuiSelect multiple value={tagsFilter} label="Tags" onChange={(e) => setTagsFilter(e.target.value)} renderValue={(s) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{s.map(v => <Chip key={v} label={v} size="small" />)}</Box>}>{allTags.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</MuiSelect></FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Trained</InputLabel><MuiSelect value={trainFilter} label="Trained" onChange={(e) => setTrainFilter(e.target.value)}><MenuItem value="all">All</MenuItem><MenuItem value="trained">Yes</MenuItem><MenuItem value="not_trained">No</MenuItem></MuiSelect></FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Auto Sync</InputLabel><MuiSelect value={autoSyncFilter} label="Auto Sync" onChange={(e) => setAutoSyncFilter(e.target.value)}><MenuItem value="all">All</MenuItem><MenuItem value="on">On</MenuItem><MenuItem value="off">Off</MenuItem></MuiSelect></FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Status</InputLabel><MuiSelect value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="all">All</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="not_pending">Not Pending</MenuItem></MuiSelect></FormControl>
        </>
    );

    const selectedRowsActions = (
        <Stack direction="row" spacing={2}>
            {permission.canModify && <MuiButton variant="contained" color="primary" size="small" onClick={handleScrapeAndTrain} disabled={isProcessingScrapeTrain} startIcon={isProcessingScrapeTrain ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}>{isProcessingScrapeTrain ? "Processing..." : "Scrape & Train"}</MuiButton>}
        </Stack>
    );

    return (
        <Box mt={2}>
            <DataTableWithFilters 
                columns={columns} 
                data={filteredFiles} 
                loading={loading} 
                error={error} 
                onErrorClose={() => setError("")} 
                filterComponents={filterComponents} 
                selectedRowsActions={selectedRowsActions} 
                selectedRowsCount={selectedRows.length} 
                onSelectedRowsChange={handleRowSelected} 
                clearSelectedRows={toggleCleared} 
                expandableRows 
                expandableRowsComponent={ExpandedComponent}
                noDataMessage="No synced files found." 
                theme={theme} 
                searchText={searchText} 
                onSearchChange={(e) => setSearchText(e.target.value)} 
                searchPlaceholder="Search by name, type, summary, or tags..."
            />
            
            {/* Edit Content Modal */}
            <MuiModal open={isEditModalOpen} onClose={closeEditModal}>
                <Box sx={muiModalStyle}>
                    {currentDocumentForModal && (
                        <EditGoogleDriveFileContent 
                            document={currentDocumentForModal} 
                            onClose={closeEditModal} 
                            onUpdate={handleUpdateFromEdit} 
                            chatbotId={chatbotId} 
                            permission={permission} 
                            categories={categories}
                        />
                    )}
                </Box>
            </MuiModal>

            {/* Edit Summary Modal */}
            <MuiModal open={isSummaryModalOpen} onClose={closeSummaryModal}>
                <Box sx={summaryModalStyle}>
                    {currentDocumentForModal && (
                        <EditGoogleDriveSummary 
                            document={currentDocumentForModal} 
                            onClose={closeSummaryModal} 
                            onUpdate={handleUpdateFromSummary} 
                            chatbotId={chatbotId} 
                            permission={permission}
                        />
                    )}
                </Box>
            </MuiModal>

            {currentDocumentForModal && <SyncSettingsModalForDrive isOpen={isSyncSettingsModalOpen} onClose={closeSyncSettingsModal} onSave={handleSaveSyncSettings} document={currentDocumentForModal} isLoading={actionLoading === 'syncSave'} permission={permission}/>}
            {currentDocumentForModal && <PendingUpdateModalForDrive isOpen={isPendingUpdateModalOpen} onClose={closePendingUpdateModal} onAccept={(html) => handleResolvePendingUpdate('accept', html)} onReject={() => handleResolvePendingUpdate('reject')} originalContentHTML={currentDocumentForModal.page_content} pendingContentHTML={currentDocumentForModal.pending_update_content} documentUrl={currentDocumentForModal.filename} isLoadingAccept={actionLoading === 'accept'} isLoadingReject={actionLoading === 'reject'} permission={permission} />}
            {isCategoryModalOpen && <ManageGDriveCategories open={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} chatbotId={chatbotId} onCategoriesUpdate={onCategoriesUpdate}/>}
            
            <style jsx global>{`
              .ant-select-dropdown { z-index: 1350 !important; }
            `}</style>
        </Box>
    );
}